<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AppDropdownMainMenu',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({});

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const dashboards = ref(null);
  const ux = ref({
    loadingPlaceholder: 'none',
  });

  const $data = computed(() => ({
    dashboards: dashboards.value,
    ux: ux.value,
  }));

  const $$load_dashboards = async () => {
    return new Promise((resolve) => {
      $fn.value
        .fetch('list_superset_dashboards')
        .then((p) => resolve(p.result))
        .catch((err) => {
          resolve({
            error: {
              message: _.get(
                err,
                'response.data.message',
                err.toJSON().message
              ),
            },
          });
        });
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'dashboards' }, { name: 'ux' }],
    loaders: { dashboards: $$load_dashboards },
    ux: { loadingPlaceholder: 'none' },
    dataRefs: { dashboards, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const sidebarCollapsed = computed(() => {
    return $app.value.sidebar ? $app.value.sidebar.collapsed : false;
  });
  const collapseIcon = computed(() => {
    return sidebarCollapsed.value ? 'mdi:dock-right' : 'mdi:dock-left';
  });

  const act_8564662037462133_script_cnnh6ul6l_value_callable = async (
    $event
  ) => {
    return $app.value.sidebar && $app.value.sidebar.toggle();
  };
  const act_8564662037462133_script_cnnh6ul6l_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8564662037462133_script_cnnh6ul6l_value_callable($event);
      },
    };
  };
  const act_3312459938551810_showModal_l9ur05s8o_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        invoiceKind: 'outgoing',
      },
    };
  };
  const act_7711295589729650_showModal_sdf63mypy_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateInvoice',
      props: {
        invoiceKind: 'incoming',
      },
    };
  };
  const act_6735074234099849_showModal_dsuvz1z79_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'cash_book',
        transactionType: 'deposit',
      },
    };
  };
  const act_1461016919552580_showModal_alihze3sx_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'cash_book',
        transactionType: 'payment',
      },
    };
  };
  const act_8159027920762481_showModal_yvew4qnd4_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Payment',
      props: {
        transactionKind: 'bank',
      },
    };
  };
  const act_2045559189368907_showModal_pwhjqx4lo_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'AccountStatementReport',
    };
  };
  const act_2649299566695801_showModal_ighd1vzjf_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCreateFixedAsset',
      props: {
        assetType: 'general',
      },
    };
  };
  const act_7751968922212722_showModal_9ubil0fcu_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCreateFixedAsset',
      props: {
        assetType: 'waste_bin',
      },
    };
  };
  const act_5596318773635798_showModal_ms8myvvcz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateMeter',
    };
  };
  const act_8608073662829418_showModal_sspnz930m_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ReplaceMeter',
    };
  };
  const act_3521576549885017_showModal_ppsle0n7d_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MeterDataExchange',
    };
  };
  const act_8596272012843862_showModal_br1ctolqz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateContract',
    };
  };
  const act_6524071025158589_showModal_1etht4sit_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BillingRules',
    };
  };
  const act_7495010493811270_showModal_2jy2q9c60_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Worksheet',
      props: {
        size: 'lg',
      },
    };
  };
  const act_6865942025371558_showModal_1nyv0c9ni_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProduct',
    };
  };
  const act_1823325530903709_showModal_63xfxyyqw_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Tariffs',
    };
  };
  const act_4588180971005511_showModal_3u873atxk_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateRental',
    };
  };
  const act_1549954609779263_showModal_arbe69odq_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProject',
    };
  };
  const act_2781228788545099_showModal_nty69tfa2_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateProperty',
    };
  };
  const act_4583127870703770_showModal_a6m2a40fg_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateCompany',
      props: {
        role: 'partner',
      },
    };
  };
  const act_7658371602827426_showModal_ay1qqjh13_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateCompany',
      props: {
        role: 'own',
      },
    };
  };

  const act_7315092382398562_reloadComponentData_ef2d4efji =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_i4v71wozl =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_ef2d4efji,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_i4v71wozl],
    event: 'reload',
  });
  const act_8564662037462133_script_cnnh6ul6l = new actionTypes.ScriptAction({
    actionArgs: act_8564662037462133_script_cnnh6ul6l_getActionArgs,
  });
  const inst_evh_8564662037462133_click_zgsec7eij =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_script_cnnh6ul6l,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_8564662037462133_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_click_zgsec7eij],
    event: 'click',
  });
  const act_3312459938551810_showModal_l9ur05s8o =
    new actionTypes.ShowModalAction({
      actionArgs: act_3312459938551810_showModal_l9ur05s8o_getActionArgs,
    });
  const inst_evh_3312459938551810_clickPrevent_ee8e9lg0c =
    new actionTypes.EventHandlerImpl({
      action: act_3312459938551810_showModal_l9ur05s8o,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_3312459938551810_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3312459938551810_clickPrevent_ee8e9lg0c],
      event: 'click.prevent',
    });
  const act_7711295589729650_showModal_sdf63mypy =
    new actionTypes.ShowModalAction({
      actionArgs: act_7711295589729650_showModal_sdf63mypy_getActionArgs,
    });
  const inst_evh_7711295589729650_clickPrevent_2sjn5b9m1 =
    new actionTypes.EventHandlerImpl({
      action: act_7711295589729650_showModal_sdf63mypy,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7711295589729650_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7711295589729650_clickPrevent_2sjn5b9m1],
      event: 'click.prevent',
    });
  const act_6735074234099849_showModal_dsuvz1z79 =
    new actionTypes.ShowModalAction({
      actionArgs: act_6735074234099849_showModal_dsuvz1z79_getActionArgs,
    });
  const inst_evh_6735074234099849_clickPrevent_zormxw85q =
    new actionTypes.EventHandlerImpl({
      action: act_6735074234099849_showModal_dsuvz1z79,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6735074234099849_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6735074234099849_clickPrevent_zormxw85q],
      event: 'click.prevent',
    });
  const act_1461016919552580_showModal_alihze3sx =
    new actionTypes.ShowModalAction({
      actionArgs: act_1461016919552580_showModal_alihze3sx_getActionArgs,
    });
  const inst_evh_1461016919552580_clickPrevent_px139n4bq =
    new actionTypes.EventHandlerImpl({
      action: act_1461016919552580_showModal_alihze3sx,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1461016919552580_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1461016919552580_clickPrevent_px139n4bq],
      event: 'click.prevent',
    });
  const act_8159027920762481_showModal_yvew4qnd4 =
    new actionTypes.ShowModalAction({
      actionArgs: act_8159027920762481_showModal_yvew4qnd4_getActionArgs,
    });
  const inst_evh_8159027920762481_clickPrevent_yosissbbg =
    new actionTypes.EventHandlerImpl({
      action: act_8159027920762481_showModal_yvew4qnd4,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8159027920762481_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8159027920762481_clickPrevent_yosissbbg],
      event: 'click.prevent',
    });
  const act_2045559189368907_showModal_pwhjqx4lo =
    new actionTypes.ShowModalAction({
      actionArgs: act_2045559189368907_showModal_pwhjqx4lo_getActionArgs,
    });
  const inst_evh_2045559189368907_clickPrevent_sgcsacr47 =
    new actionTypes.EventHandlerImpl({
      action: act_2045559189368907_showModal_pwhjqx4lo,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2045559189368907_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2045559189368907_clickPrevent_sgcsacr47],
      event: 'click.prevent',
    });
  const act_2649299566695801_showModal_ighd1vzjf =
    new actionTypes.ShowModalAction({
      actionArgs: act_2649299566695801_showModal_ighd1vzjf_getActionArgs,
    });
  const inst_evh_2649299566695801_clickPrevent_yuwo1dfob =
    new actionTypes.EventHandlerImpl({
      action: act_2649299566695801_showModal_ighd1vzjf,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2649299566695801_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2649299566695801_clickPrevent_yuwo1dfob],
      event: 'click.prevent',
    });
  const act_7751968922212722_showModal_9ubil0fcu =
    new actionTypes.ShowModalAction({
      actionArgs: act_7751968922212722_showModal_9ubil0fcu_getActionArgs,
    });
  const inst_evh_7751968922212722_clickPrevent_kw11my060 =
    new actionTypes.EventHandlerImpl({
      action: act_7751968922212722_showModal_9ubil0fcu,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7751968922212722_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7751968922212722_clickPrevent_kw11my060],
      event: 'click.prevent',
    });
  const act_5596318773635798_showModal_ms8myvvcz =
    new actionTypes.ShowModalAction({
      actionArgs: act_5596318773635798_showModal_ms8myvvcz_getActionArgs,
    });
  const inst_evh_5596318773635798_clickPrevent_wfywapmn3 =
    new actionTypes.EventHandlerImpl({
      action: act_5596318773635798_showModal_ms8myvvcz,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_5596318773635798_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5596318773635798_clickPrevent_wfywapmn3],
      event: 'click.prevent',
    });
  const act_8608073662829418_showModal_sspnz930m =
    new actionTypes.ShowModalAction({
      actionArgs: act_8608073662829418_showModal_sspnz930m_getActionArgs,
    });
  const inst_evh_8608073662829418_clickPrevent_i2gdjilvh =
    new actionTypes.EventHandlerImpl({
      action: act_8608073662829418_showModal_sspnz930m,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8608073662829418_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8608073662829418_clickPrevent_i2gdjilvh],
      event: 'click.prevent',
    });
  const act_3521576549885017_showModal_ppsle0n7d =
    new actionTypes.ShowModalAction({
      actionArgs: act_3521576549885017_showModal_ppsle0n7d_getActionArgs,
    });
  const inst_evh_3521576549885017_clickPrevent_vhsblpvn7 =
    new actionTypes.EventHandlerImpl({
      action: act_3521576549885017_showModal_ppsle0n7d,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_3521576549885017_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3521576549885017_clickPrevent_vhsblpvn7],
      event: 'click.prevent',
    });
  const act_8596272012843862_showModal_br1ctolqz =
    new actionTypes.ShowModalAction({
      actionArgs: act_8596272012843862_showModal_br1ctolqz_getActionArgs,
    });
  const inst_evh_8596272012843862_clickPrevent_oqxkgfrdi =
    new actionTypes.EventHandlerImpl({
      action: act_8596272012843862_showModal_br1ctolqz,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_8596272012843862_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8596272012843862_clickPrevent_oqxkgfrdi],
      event: 'click.prevent',
    });
  const act_6524071025158589_showModal_1etht4sit =
    new actionTypes.ShowModalAction({
      actionArgs: act_6524071025158589_showModal_1etht4sit_getActionArgs,
    });
  const inst_evh_6524071025158589_clickPrevent_z4swlpa24 =
    new actionTypes.EventHandlerImpl({
      action: act_6524071025158589_showModal_1etht4sit,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6524071025158589_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6524071025158589_clickPrevent_z4swlpa24],
      event: 'click.prevent',
    });
  const act_7495010493811270_showModal_2jy2q9c60 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7495010493811270_showModal_2jy2q9c60_getActionArgs,
    });
  const inst_evh_7495010493811270_clickPrevent_qj3h5jed7 =
    new actionTypes.EventHandlerImpl({
      action: act_7495010493811270_showModal_2jy2q9c60,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7495010493811270_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7495010493811270_clickPrevent_qj3h5jed7],
      event: 'click.prevent',
    });
  const act_6865942025371558_showModal_1nyv0c9ni =
    new actionTypes.ShowModalAction({
      actionArgs: act_6865942025371558_showModal_1nyv0c9ni_getActionArgs,
    });
  const inst_evh_6865942025371558_clickPrevent_uv56a65nb =
    new actionTypes.EventHandlerImpl({
      action: act_6865942025371558_showModal_1nyv0c9ni,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_6865942025371558_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6865942025371558_clickPrevent_uv56a65nb],
      event: 'click.prevent',
    });
  const act_1823325530903709_showModal_63xfxyyqw =
    new actionTypes.ShowModalAction({
      actionArgs: act_1823325530903709_showModal_63xfxyyqw_getActionArgs,
    });
  const inst_evh_1823325530903709_clickPrevent_gomb9tfx7 =
    new actionTypes.EventHandlerImpl({
      action: act_1823325530903709_showModal_63xfxyyqw,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1823325530903709_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1823325530903709_clickPrevent_gomb9tfx7],
      event: 'click.prevent',
    });
  const act_4588180971005511_showModal_3u873atxk =
    new actionTypes.ShowModalAction({
      actionArgs: act_4588180971005511_showModal_3u873atxk_getActionArgs,
    });
  const inst_evh_4588180971005511_clickPrevent_0i7mw7pbk =
    new actionTypes.EventHandlerImpl({
      action: act_4588180971005511_showModal_3u873atxk,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_4588180971005511_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4588180971005511_clickPrevent_0i7mw7pbk],
      event: 'click.prevent',
    });
  const act_1549954609779263_showModal_arbe69odq =
    new actionTypes.ShowModalAction({
      actionArgs: act_1549954609779263_showModal_arbe69odq_getActionArgs,
    });
  const inst_evh_1549954609779263_clickPrevent_aky4wpaoi =
    new actionTypes.EventHandlerImpl({
      action: act_1549954609779263_showModal_arbe69odq,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_1549954609779263_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1549954609779263_clickPrevent_aky4wpaoi],
      event: 'click.prevent',
    });
  const act_2781228788545099_showModal_nty69tfa2 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2781228788545099_showModal_nty69tfa2_getActionArgs,
    });
  const inst_evh_2781228788545099_clickPrevent_223ipugcn =
    new actionTypes.EventHandlerImpl({
      action: act_2781228788545099_showModal_nty69tfa2,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_2781228788545099_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2781228788545099_clickPrevent_223ipugcn],
      event: 'click.prevent',
    });
  const act_4583127870703770_showModal_a6m2a40fg =
    new actionTypes.ShowModalAction({
      actionArgs: act_4583127870703770_showModal_a6m2a40fg_getActionArgs,
    });
  const inst_evh_4583127870703770_clickPrevent_3aa81bwkz =
    new actionTypes.EventHandlerImpl({
      action: act_4583127870703770_showModal_a6m2a40fg,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_4583127870703770_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4583127870703770_clickPrevent_3aa81bwkz],
      event: 'click.prevent',
    });
  const act_7658371602827426_showModal_ay1qqjh13 =
    new actionTypes.ShowModalAction({
      actionArgs: act_7658371602827426_showModal_ay1qqjh13_getActionArgs,
    });
  const inst_evh_7658371602827426_clickPrevent_qxhob38cy =
    new actionTypes.EventHandlerImpl({
      action: act_7658371602827426_showModal_ay1qqjh13,
      event: 'click.prevent',
      displayName: 'showModal',
    });
  const inst_evh_7658371602827426_clickPrevent =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7658371602827426_clickPrevent_qxhob38cy],
      event: 'click.prevent',
    });

  const actions = {
    inst_evh_7315092382398562_reload_i4v71wozl,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_click_zgsec7eij,
    inst_evh_8564662037462133_click,
    inst_evh_3312459938551810_clickPrevent_ee8e9lg0c,
    inst_evh_3312459938551810_clickPrevent,
    inst_evh_7711295589729650_clickPrevent_2sjn5b9m1,
    inst_evh_7711295589729650_clickPrevent,
    inst_evh_6735074234099849_clickPrevent_zormxw85q,
    inst_evh_6735074234099849_clickPrevent,
    inst_evh_1461016919552580_clickPrevent_px139n4bq,
    inst_evh_1461016919552580_clickPrevent,
    inst_evh_8159027920762481_clickPrevent_yosissbbg,
    inst_evh_8159027920762481_clickPrevent,
    inst_evh_2045559189368907_clickPrevent_sgcsacr47,
    inst_evh_2045559189368907_clickPrevent,
    inst_evh_2649299566695801_clickPrevent_yuwo1dfob,
    inst_evh_2649299566695801_clickPrevent,
    inst_evh_7751968922212722_clickPrevent_kw11my060,
    inst_evh_7751968922212722_clickPrevent,
    inst_evh_5596318773635798_clickPrevent_wfywapmn3,
    inst_evh_5596318773635798_clickPrevent,
    inst_evh_8608073662829418_clickPrevent_i2gdjilvh,
    inst_evh_8608073662829418_clickPrevent,
    inst_evh_3521576549885017_clickPrevent_vhsblpvn7,
    inst_evh_3521576549885017_clickPrevent,
    inst_evh_8596272012843862_clickPrevent_oqxkgfrdi,
    inst_evh_8596272012843862_clickPrevent,
    inst_evh_6524071025158589_clickPrevent_z4swlpa24,
    inst_evh_6524071025158589_clickPrevent,
    inst_evh_7495010493811270_clickPrevent_qj3h5jed7,
    inst_evh_7495010493811270_clickPrevent,
    inst_evh_6865942025371558_clickPrevent_uv56a65nb,
    inst_evh_6865942025371558_clickPrevent,
    inst_evh_1823325530903709_clickPrevent_gomb9tfx7,
    inst_evh_1823325530903709_clickPrevent,
    inst_evh_4588180971005511_clickPrevent_0i7mw7pbk,
    inst_evh_4588180971005511_clickPrevent,
    inst_evh_1549954609779263_clickPrevent_aky4wpaoi,
    inst_evh_1549954609779263_clickPrevent,
    inst_evh_2781228788545099_clickPrevent_223ipugcn,
    inst_evh_2781228788545099_clickPrevent,
    inst_evh_4583127870703770_clickPrevent_3aa81bwkz,
    inst_evh_4583127870703770_clickPrevent,
    inst_evh_7658371602827426_clickPrevent_qxhob38cy,
    inst_evh_7658371602827426_clickPrevent,
  };

  const evh_7315092382398562_reload_i4v71wozl = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_i4v71wozl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_click_zgsec7eij = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_click_zgsec7eij.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_click = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3312459938551810_clickPrevent_ee8e9lg0c = async (event, scope) => {
    return await actions.inst_evh_3312459938551810_clickPrevent_ee8e9lg0c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3312459938551810_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_3312459938551810_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7711295589729650_clickPrevent_2sjn5b9m1 = async (event, scope) => {
    return await actions.inst_evh_7711295589729650_clickPrevent_2sjn5b9m1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7711295589729650_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7711295589729650_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6735074234099849_clickPrevent_zormxw85q = async (event, scope) => {
    return await actions.inst_evh_6735074234099849_clickPrevent_zormxw85q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6735074234099849_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6735074234099849_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1461016919552580_clickPrevent_px139n4bq = async (event, scope) => {
    return await actions.inst_evh_1461016919552580_clickPrevent_px139n4bq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1461016919552580_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1461016919552580_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8159027920762481_clickPrevent_yosissbbg = async (event, scope) => {
    return await actions.inst_evh_8159027920762481_clickPrevent_yosissbbg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8159027920762481_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8159027920762481_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2045559189368907_clickPrevent_sgcsacr47 = async (event, scope) => {
    return await actions.inst_evh_2045559189368907_clickPrevent_sgcsacr47.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2045559189368907_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2045559189368907_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2649299566695801_clickPrevent_yuwo1dfob = async (event, scope) => {
    return await actions.inst_evh_2649299566695801_clickPrevent_yuwo1dfob.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2649299566695801_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2649299566695801_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7751968922212722_clickPrevent_kw11my060 = async (event, scope) => {
    return await actions.inst_evh_7751968922212722_clickPrevent_kw11my060.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7751968922212722_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7751968922212722_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5596318773635798_clickPrevent_wfywapmn3 = async (event, scope) => {
    return await actions.inst_evh_5596318773635798_clickPrevent_wfywapmn3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5596318773635798_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_5596318773635798_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8608073662829418_clickPrevent_i2gdjilvh = async (event, scope) => {
    return await actions.inst_evh_8608073662829418_clickPrevent_i2gdjilvh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8608073662829418_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8608073662829418_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3521576549885017_clickPrevent_vhsblpvn7 = async (event, scope) => {
    return await actions.inst_evh_3521576549885017_clickPrevent_vhsblpvn7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3521576549885017_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_3521576549885017_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8596272012843862_clickPrevent_oqxkgfrdi = async (event, scope) => {
    return await actions.inst_evh_8596272012843862_clickPrevent_oqxkgfrdi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8596272012843862_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_8596272012843862_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6524071025158589_clickPrevent_z4swlpa24 = async (event, scope) => {
    return await actions.inst_evh_6524071025158589_clickPrevent_z4swlpa24.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6524071025158589_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6524071025158589_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7495010493811270_clickPrevent_qj3h5jed7 = async (event, scope) => {
    return await actions.inst_evh_7495010493811270_clickPrevent_qj3h5jed7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7495010493811270_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7495010493811270_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6865942025371558_clickPrevent_uv56a65nb = async (event, scope) => {
    return await actions.inst_evh_6865942025371558_clickPrevent_uv56a65nb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6865942025371558_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_6865942025371558_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1823325530903709_clickPrevent_gomb9tfx7 = async (event, scope) => {
    return await actions.inst_evh_1823325530903709_clickPrevent_gomb9tfx7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1823325530903709_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1823325530903709_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4588180971005511_clickPrevent_0i7mw7pbk = async (event, scope) => {
    return await actions.inst_evh_4588180971005511_clickPrevent_0i7mw7pbk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4588180971005511_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_4588180971005511_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1549954609779263_clickPrevent_aky4wpaoi = async (event, scope) => {
    return await actions.inst_evh_1549954609779263_clickPrevent_aky4wpaoi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1549954609779263_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_1549954609779263_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2781228788545099_clickPrevent_223ipugcn = async (event, scope) => {
    return await actions.inst_evh_2781228788545099_clickPrevent_223ipugcn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2781228788545099_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_2781228788545099_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4583127870703770_clickPrevent_3aa81bwkz = async (event, scope) => {
    return await actions.inst_evh_4583127870703770_clickPrevent_3aa81bwkz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4583127870703770_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_4583127870703770_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7658371602827426_clickPrevent_qxhob38cy = async (event, scope) => {
    return await actions.inst_evh_7658371602827426_clickPrevent_qxhob38cy.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7658371602827426_clickPrevent = async (event, scope) => {
    return await actions.inst_evh_7658371602827426_clickPrevent.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    loadingPlaceholder="none"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <BNavbarNav class="main-menu-sidebar">
      <button
        type="button"
        :title="pgettext('SidebarHeader', 'Toggle sidebar')"
        class="main-menu-sidebar__collapse-btn"
        @click="evh_8564662037462133_click($event, {})"
      >
        <ItpIcon :name="collapseIcon"> </ItpIcon>
      </button>
      <ItpAppsDropdown
        icon="mdi:dots-grid"
        :title="pgettext('MainMenu', 'Apps')"
      >
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Finance')">
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Register') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/registers"
            :icon="$config.register.icon"
            :text="pgettext('MainMenu', 'Registers')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Customer invoices') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/invoices-out"
            :icon="$config.invoice.customerInvoiceIcon"
            :text="pgettext('MainMenu', 'Customer invoices')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New Customer Invoice...')"
            :icon="$config.invoice.customerInvoiceIcon"
            dropdown-item
            @click.prevent="evh_3312459938551810_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/billing-list"
            :icon="$config.billingList.icon"
            :text="pgettext('MainMenu', 'Billing List')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/invoice-generation"
            :icon="$config.invoiceGeneration.icon"
            :text="pgettext('MainMenu', 'Invoice Generation')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Incoming invoices') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/invoices-in"
            :icon="$config.invoice.vendorInvoiceIcon"
            :text="pgettext('MainMenu', 'Incoming invoices')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New Incoming Invoice...')"
            :icon="$config.invoice.icon"
            dropdown-item
            @click.prevent="evh_7711295589729650_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Payments') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/payments"
            :icon="$config.payment.paymentsList.icon"
            :text="pgettext('PaymentsView', 'Payments')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/accounts-daily-flow"
            :icon="$config.accountsDailyFlow.icon"
            :text="pgettext('MainMenu', 'Accounts Daily Flow')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('Create Payment', 'New Cash In...')"
            :icon="$config.payment.cash.icon"
            dropdown-item
            @click.prevent="evh_6735074234099849_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('Create Payment', 'New Cash Out...')"
            :icon="$config.payment.cash.icon"
            dropdown-item
            @click.prevent="evh_1461016919552580_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('Create Payment', 'New Banking...')"
            :icon="$config.payment.banking.icon"
            dropdown-item
            @click.prevent="evh_8159027920762481_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MeinMenu', 'Account statement report...')"
            :icon="$config.accountStatementReport.icon"
            dropdown-item
            @click.prevent="evh_2045559189368907_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Claims management') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/claims"
            :icon="$config.claim.icon"
            :text="pgettext('MainMenu', 'Claims')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/dunning-letters"
            :icon="$config.dunningLetter.icon"
            :text="pgettext('MainMenu', 'Dunning letters')"
            dropdown-item
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Assets')">
          <ItpNavItem
            to="/fixed-assets"
            :icon="$config.fixedAsset.icon"
            :text="pgettext('MainMenu', 'Fixed Assets')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Create Asset...')"
            :icon="$config.fixedAsset.icon"
            dropdown-item
            @click.prevent="evh_2649299566695801_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/waste-bins"
            :icon="$config.wasteBin.icon"
            :text="pgettext('MainMenu', 'Waste Bins')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/waste-bin-readings"
            :icon="$config.wasteBin.icon"
            :text="pgettext('MainMenu', 'Waste Bin Readings')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Create Waste Bin...')"
            :icon="$config.wasteBin.createWasteBinIcon"
            dropdown-item
            @click.prevent="evh_7751968922212722_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpDropdownText>
            {{ pgettext('MainMenu', 'Meters') }}
          </ItpDropdownText>
          <ItpNavItem
            to="/meters"
            :icon="$config.meter.icon"
            :text="pgettext('MainMenu', 'Meters')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add Meter...')"
            :icon="$config.meter.addMeterIcon"
            dropdown-item
            @click.prevent="evh_5596318773635798_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            href="#"
            :icon="$config.meterReplacement.icon"
            :text="pgettext('MainMenu', 'Replace meter...')"
            dropdown-item
            @click.prevent="evh_8608073662829418_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/auto-readings"
            :icon="$config.meterAutoReading.icon"
            :text="pgettext('MainMenu', 'Auto Readings')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            href="#"
            icon="fa-upload"
            :text="pgettext('MainMenu', 'Meter Data Exchange...')"
            dropdown-item
            @click.prevent="evh_3521576549885017_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/meter-consumptions"
            :icon="$config.meterConsumption.icon"
            :text="pgettext('MainMenu', 'Meter consumptions')"
            dropdown-item
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Contracts')">
          <ItpNavItem
            to="/contracts"
            :icon="$config.contract.icon"
            :text="pgettext('MainMenu', 'Contracts')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Create Contract...')"
            :icon="$config.contract.icon"
            dropdown-item
            @click.prevent="evh_8596272012843862_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-services"
            :icon="$config.contract.icon"
            :text="pgettext('MainMenu', 'Contract services')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-service-fees"
            :icon="$config.contract.icon"
            :text="pgettext('MainMenu', 'Contract service fees')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-attachments"
            :icon="$config.attachmentIcon"
            :text="pgettext('MainMenu', 'Contract attachments')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            to="/contract-events"
            :icon="$config.contract.views.contractEvents.icon"
            :text="pgettext('MainMenu', 'Contract events')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/overlapping-revisions"
            :icon="$config.contract.views.overlappingRevisions.icon"
            :text="pgettext('MainMenu', 'Overlapping Revisions')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            href="#"
            icon="gravity-ui:curly-brackets-function"
            :text="pgettext('MainMenu', 'Billing rules')"
            dropdown-item
            @click.prevent="evh_6524071025158589_clickPrevent($event, {})"
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Worksheets')">
          <ItpNavItem
            to="/worksheets"
            :icon="$config.worksheet.icon"
            :text="pgettext('MainMenu', 'Worksheets')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New Worksheet...')"
            :icon="$config.worksheet.createWorksheetIcon"
            dropdown-item
            @click.prevent="evh_7495010493811270_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/worksheets-work-summary"
            :icon="$config.worksheet.icon"
            :text="pgettext('MainMenu', 'Work summary')"
            dropdown-item
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Master data')">
          <ItpNavItem
            to="/products"
            :icon="$config.product.icon"
            :text="pgettext('MainMenu', 'Products')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'New product/service...')"
            :icon="$config.product.addProductIcon"
            dropdown-item
            @click.prevent="evh_6865942025371558_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Tariffs...')"
            :icon="$config.tariff.icon"
            dropdown-item
            @click.prevent="evh_1823325530903709_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/rentals"
            :icon="$config.rental.icon"
            :text="pgettext('MainMenu', 'Rentals')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add Rental...')"
            :icon="$config.rental.addRentalIcon"
            dropdown-item
            @click.prevent="evh_4588180971005511_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpNavItem
            to="/rental-services"
            :icon="$config.rental.icon"
            :text="pgettext('MainMenu', 'Rental services')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/projects"
            :icon="$config.project.icon"
            :text="pgettext('MainMenu', 'Projects')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add project...')"
            :icon="$config.project.addProjectIcon"
            dropdown-item
            @click.prevent="evh_1549954609779263_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/properties"
            :icon="$config.property.icon"
            :text="pgettext('MainMenu', 'Locations')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add property...')"
            :icon="$config.property.addPropertyIcon"
            dropdown-item
            @click.prevent="evh_2781228788545099_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/partners"
            :icon="$config.company.icons.partner"
            :text="pgettext('MainMenu', 'Partners')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add partner...')"
            :icon="$config.company.icons.partner"
            dropdown-item
            @click.prevent="evh_4583127870703770_clickPrevent($event, {})"
          >
          </ItpNavItem>
          <ItpDropdownDivider> </ItpDropdownDivider>
          <ItpNavItem
            to="/own-companies"
            :icon="$config.company.icons.own"
            :text="pgettext('MainMenu', 'Own companies')"
            dropdown-item
          >
          </ItpNavItem>
          <ItpNavItem
            :text="pgettext('MainMenu', 'Add company...')"
            :icon="$config.company.icons.own"
            dropdown-item
            @click.prevent="evh_7658371602827426_clickPrevent($event, {})"
          >
          </ItpNavItem>
        </ItpDropdownSubmenu>
        <ItpDropdownSubmenu :label="pgettext('MainMenu', 'Reports')">
          <ItpNavItem
            v-if="dashboards && dashboards.error && dashboards.error.message"
          >
            <ItpText
              wrap
              :title="dashboards.error.message"
              :text="dashboards.error.message"
            >
            </ItpText>
          </ItpNavItem>
          <template v-for="item in dashboards">
            <ItpNavItem
              :key="item.id"
              :to="'/charts/' + item.id"
              :text="item.dashboard_title"
              dropdown-item
              v-if="_.isArray(dashboards)"
            >
            </ItpNavItem>
          </template>
        </ItpDropdownSubmenu>
      </ItpAppsDropdown>
    </BNavbarNav>
  </ItpUsercontrol>
</template>
